import React, { useState, useEffect } from "react";
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Button,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon, School as SchoolIcon } from "@mui/icons-material";

const TymTbl = () => {
  const [timetableData, setTimetableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTimeTable = async () => {
      try {
        const response = await fetch(
          "https://wsmsv1.aidigitalsec.com/api/TimeTable/GetTimeTable",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: sessionStorage.getItem("token"),
            },
            body: JSON.stringify({
              classId: sessionStorage.getItem("classId") || 0,
              sectionId: sessionStorage.getItem("sectionId") || 0,
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch timetable");
        }

        const data = await response.json();

        if (
          !data ||
          (data.msg && data.msg.toLowerCase().includes("not found"))
        ) {
          setTimetableData([]);
        } else if (Array.isArray(data)) {
          setTimetableData(data);
        } else {
          setTimetableData([]);
        }
      } catch (error) {
        console.error("Error fetching timetable:", error);
        setError("Unable to load timetable. Please try again later.");
        setTimetableData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTimeTable();
  }, []);

  // Group timetable data by period
  const groupedData = timetableData.reduce((acc, row) => {
    const period = row.periodSequenceNo;
    if (!acc[period]) {
      acc[period] = [];
    }
    acc[period].push(row);
    return acc;
  }, {});

  const periods = Array.from(
    new Set(timetableData.map((item) => item.periodSequenceNo))
  ).sort((a, b) => a - b);

  const daysOfWeek = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  // Loading State
  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="70vh"
        textAlign="center"
      >
        <CircularProgress size={60} thickness={5} sx={{ mb: 2 }} />
        <Typography variant="h6" color="textPrimary">
          Loading timetable...
        </Typography>
      </Box>
    );
  }

  // Error State
  if (error) {
    return (
      <Box textAlign="center" mt={8} px={3}>
        <Typography variant="h6" color="error" gutterBottom>
          Oops! Something went wrong.
        </Typography>
        <Typography variant="body1" color="textSecondary">
          {error}
        </Typography>
      </Box>
    );
  }

  // Empty State
  if (timetableData.length === 0) {
    return (
      <Container maxWidth="md">
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="70vh"
          textAlign="center"
          px={3}
        >
          <SchoolIcon sx={{ fontSize: 120, color: "#bbdefb", mb: 4 }} />
          <Typography
            variant="h5"
            gutterBottom
            color="textPrimary"
            fontWeight="medium"
          >
            No Timetable Available
          </Typography>
          <Typography
            variant="body1"
            color="textSecondary"
            maxWidth="600px"
            mx="auto"
            paragraph
          >
            There is currently no timetable assigned for your class and section.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Please contact your school administration or check back later for updates.
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <>
      

      <Typography
        variant="h4"
        gutterBottom
        align="center"
        fontWeight="bold"
        color="#333"
        mb={1}
      >
        Class Timetable
      </Typography>

      <TableContainer component={Paper} elevation={6} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                colSpan={3}
                align="center"
                sx={{
                  background: 'linear-gradient(135deg, #080F1F 0%, #102A43 55%, #1B4965 100%)',
                  color: "white",
                  fontWeight: "bold",
                  fontSize: "1.1rem",
                  py: 2,
                }}
              >
                Time
              </TableCell>
              {daysOfWeek.map((day) => (
                <TableCell
                  key={day}
                  align="center"
                  sx={{
                    background: 'linear-gradient(135deg, #080F1F 0%, #102A43 55%, #1B4965 100%)',
                    color: "white",
                    fontWeight: "bold",
                    py: 2,
                  }}
                >
                  {day}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell
                sx={{
                  background: 'linear-gradient(135deg, #080F1F 0%, #102A43 55%, #1B4965 100%)',
                  color: "white",
                  fontWeight: "bold",
                  py: 1,
                }}
              >
                Start Time
              </TableCell>
              <TableCell
                sx={{
                  background: 'linear-gradient(135deg, #080F1F 0%, #102A43 55%, #1B4965 100%)',
                  color: "white",
                  fontWeight: "bold",
                  py: 1,
                }}
              >
                End Time
              </TableCell>
              <TableCell
                sx={{
                  background: 'linear-gradient(135deg, #080F1F 0%, #102A43 55%, #1B4965 100%)',
                  color: "white",
                  fontWeight: "bold",
                  py: 1,
                }}
              >
                Period
              </TableCell>
              {daysOfWeek.map((day) => (
                <TableCell
                  key={day}
                  sx={{
                    background: 'linear-gradient(135deg, #080F1F 0%, #102A43 55%, #1B4965 100%)',
                    py: 1,
                  }}
                />
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {periods.map((period) => {
              const periodRows = groupedData[period];
              const sampleRow = periodRows[0];

              return (
                <TableRow key={period} hover sx={{ "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" } }}>
                  <TableCell sx={{ fontWeight: "bold", borderRight: "1px solid #e0e0e0" }}>
                    {sampleRow?.startTime || "-"}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", borderRight: "1px solid #e0e0e0" }}>
                    {sampleRow?.endTime || "-"}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", borderRight: "1px solid #e0e0e0" }}>
                    Period {period}
                  </TableCell>
                  {daysOfWeek.map((day) => {
                    const entry = periodRows.find((row) => row.dayOfWeek === day);
                    return (
                      <TableCell
                        key={day}
                        align="center"
                        sx={{
                          verticalAlign: "top",
                          minHeight: 100,
                          borderRight: "1px solid #e0e0e0",
                          backgroundColor: entry ? "#f0f9ff" : "#fff",
                        }}
                      >
                        {entry ? (
                          <Box>
                            <Typography
                              variant="subtitle2"
                              fontWeight="bold"
                              color="primary"
                            >
                              {entry.subjectName}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {entry.teacherName || "Teacher"}
                            </Typography>
                            {entry.roomNo && (
                              <Typography variant="caption" color="textSecondary">
                                Room {entry.roomNo}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default TymTbl;
